import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, MessageCircle, Loader2 } from "lucide-react";
import { API, waLink } from "../config";
import { AIRCRAFT_CATEGORIES } from "../data/aircraft";
import { EASE, Overline } from "./motion";

const EMPTY = {
  departure: "",
  destination: "",
  departure_date: "",
  return_date: "",
  trip_type: "one-way",
  passengers: "",
  preferred_aircraft: "",
  name: "",
  email: "",
  phone: "",
  whatsapp: "",
  company: "",
  special_requirements: "",
};

const inputCls =
  "w-full bg-transparent border-b border-black/15 py-3 text-[15px] placeholder:text-black/30 focus:border-black focus:outline-none transition-colors duration-300";

const Field = ({ label, id, children }) => (
  <label htmlFor={id} className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-black/45">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

export const EnquiryForm = ({ source = "website", title = "Request a Charter", compact = false }) => {
  const [params] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    setForm((f) => ({
      ...f,
      departure: params.get("departure") || f.departure,
      destination: params.get("destination") || f.destination,
      preferred_aircraft: params.get("aircraft") || f.preferred_aircraft,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState("submitting");
    setError("");
    try {
      await axios.post(`${API}/leads`, { ...form, source });
      setState("success");
    } catch (err) {
      setState("error");
      setError("Something went wrong while sending your enquiry. Please try again or reach us on WhatsApp.");
    }
  };

  if (state === "success") {
    return (
      <motion.div
        data-testid="enquiry-success"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: EASE }}
        className="border border-black/10 bg-white p-10 sm:p-14"
      >
        <CheckCircle2 size={28} className="text-black" />
        <h3 className="mt-6 font-display text-2xl sm:text-3xl tracking-tight">Thank you.</h3>
        <p className="mt-4 max-w-md text-[15px] leading-relaxed text-black/60">
          Our charter team will review your requirement and contact you with suitable aircraft options.
        </p>
        <p className="mt-3 text-[13px] text-black/45">
          Every trip is sourced individually — we do not quote instant prices or guarantee availability online.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={waLink("Hello — I have just submitted a charter enquiry and would like to discuss it.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="success-whatsapp-cta"
            className="inline-flex items-center gap-2 bg-black px-6 py-3 text-[11px] uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-black/80"
          >
            <MessageCircle size={14} /> WhatsApp us
          </a>
          <button
            onClick={() => {
              setForm(EMPTY);
              setState("idle");
            }}
            data-testid="submit-another-button"
            className="border border-black/20 px-6 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-black hover:text-white"
          >
            Submit another enquiry
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="charter-enquiry-form" className={compact ? "" : "border border-black/10 bg-white p-6 sm:p-12"}>
      {!compact && (
        <div className="mb-10">
          <Overline>Charter enquiry</Overline>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight">{title}</h2>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-black/55">
            Tell us where you need to be. Our charter team sources aircraft through our global operator
            network and responds with suitable options.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-7">
        <Field label="Departure" id="f-departure">
          <input id="f-departure" data-testid="departure-input" required value={form.departure} onChange={set("departure")} placeholder="City or airport" className={inputCls} />
        </Field>
        <Field label="Destination" id="f-destination">
          <input id="f-destination" data-testid="destination-input" required value={form.destination} onChange={set("destination")} placeholder="City or airport" className={inputCls} />
        </Field>
        <Field label="Departure date" id="f-dep-date">
          <input id="f-dep-date" data-testid="departure-date-input" type="date" value={form.departure_date} onChange={set("departure_date")} className={inputCls} />
        </Field>
        <Field label="Return date" id="f-ret-date">
          <input id="f-ret-date" data-testid="return-date-input" type="date" disabled={form.trip_type === "one-way"} value={form.return_date} onChange={set("return_date")} className={`${inputCls} disabled:opacity-30`} />
        </Field>

        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-black/45">Trip</span>
          <div className="mt-3 flex gap-2" data-testid="trip-type-toggle">
            {[
              ["one-way", "One-way"],
              ["round-trip", "Round trip"],
            ].map(([val, label]) => (
              <button
                key={val}
                type="button"
                data-testid={`trip-${val}`}
                onClick={() => setForm({ ...form, trip_type: val, ...(val === "one-way" ? { return_date: "" } : {}) })}
                className={`border px-5 py-2 text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                  form.trip_type === val ? "border-black bg-black text-white" : "border-black/20 hover:border-black"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <Field label="Passengers" id="f-pax">
          <select id="f-pax" data-testid="passengers-select" value={form.passengers} onChange={set("passengers")} className={inputCls}>
            <option value="">Select</option>
            {["1–2", "3–5", "6–9", "10–15", "16+", "Group charter"].map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </Field>

        <Field label="Preferred aircraft type" id="f-aircraft">
          <select id="f-aircraft" data-testid="aircraft-select" value={form.preferred_aircraft} onChange={set("preferred_aircraft")} className={inputCls}>
            <option value="">No preference — advise me</option>
            {AIRCRAFT_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
            {form.preferred_aircraft && !AIRCRAFT_CATEGORIES.includes(form.preferred_aircraft) && (
              <option value={form.preferred_aircraft}>{form.preferred_aircraft}</option>
            )}
          </select>
        </Field>

        <Field label="Company (optional)" id="f-company">
          <input id="f-company" data-testid="company-input" value={form.company} onChange={set("company")} placeholder="Company name" className={inputCls} />
        </Field>

        <Field label="Full name" id="f-name">
          <input id="f-name" data-testid="name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
        </Field>
        <Field label="Email" id="f-email">
          <input id="f-email" data-testid="email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
        </Field>
        <Field label="Phone" id="f-phone">
          <input id="f-phone" data-testid="phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="+971 …" className={inputCls} />
        </Field>
        <Field label="WhatsApp" id="f-whatsapp">
          <input id="f-whatsapp" data-testid="whatsapp-input" type="tel" value={form.whatsapp} onChange={set("whatsapp")} placeholder="If different from phone" className={inputCls} />
        </Field>
      </div>

      <div className="mt-7">
        <Field label="Special requirements" id="f-special">
          <textarea
            id="f-special"
            data-testid="special-requirements-input"
            rows={3}
            value={form.special_requirements}
            onChange={set("special_requirements")}
            placeholder="Catering, pets, ground transport, security, flexible timing…"
            className={`${inputCls} resize-none`}
          />
        </Field>
      </div>

      {error && (
        <p data-testid="enquiry-error" className="mt-6 text-sm text-red-700">{error}</p>
      )}

      <div className="mt-10 flex flex-wrap items-center gap-5">
        <button
          type="submit"
          data-testid="request-quote-submit"
          disabled={state === "submitting"}
          className="group inline-flex items-center gap-3 bg-black px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-black/80 disabled:opacity-50"
        >
          {state === "submitting" ? (
            <>
              Sending <Loader2 size={15} className="animate-spin" />
            </>
          ) : (
            <>
              Request a Quote
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </>
          )}
        </button>
        <p className="max-w-xs text-[11px] leading-relaxed text-black/40">
          Sourced individually through independent aircraft operators. No instant pricing, no obligation.
        </p>
      </div>
    </form>
  );
};
